/**
 * CompaniesListPage — `/admin/companies` unified ISA + MSA list.
 *
 * Matches the design artboard
 * (`.design-incoming/admin-20260522/source/admin.jsx` CompaniesPage):
 *
 *   - Header: eyebrow "INTERNAL ADMIN" + H1 "Companies" + subtitle
 *     "{N} ISAs · {M} MSAs" + actions (Export CSV, + New company).
 *   - A single `Card` holding an in-card filter bar (search pill +
 *     Type select + Status select + "{n} of {m}" count) above the
 *     table.
 *   - Desktop table (≥ 900 px), 7 columns: COMPANY (TypeChip + name +
 *     /slug mono) | STATUS | REGION | PROPERTIES | USERS | CREATED |
 *     chevron. MSA rows show their aggregated property/user counts
 *     (per the artboard + the MSA detail "Aggregated properties").
 *   - Mobile card-rows (< 900 px).
 *
 * Earlier this surface was built to `COMPANIES_SPEC.md` (4 columns,
 * chip filters, no eyebrow/Export). That spec had drifted from the
 * artboard; the artboard + the reviewed screenshots are the source of
 * truth, so this is rebuilt to them.
 *
 * Phase-1: `useCompanies()` returns `[]` (no backend) → "No companies
 * yet" empty state. A future ticket swaps the stub for a TanStack
 * `GET /admin/companies` hook. Tests drive the populated table via the
 * `companiesOverride` prop.
 */

import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, Icon } from '@sparta/ui-kit';
import {
  SystemStatusBadge,
  type SystemStatus,
} from '../_components/SystemStatusBadge.js';
import { TypeChip, type CompanyType } from '../_components/TypeChip.js';
import { useCompactMode } from '../../../shared/hooks/useCompactMode.js';

// ── Local domain types ──────────────────────────────────────────
//
// Inlined while the BE shape is soft-suggestion. Lift into
// `modules/platform/api/types.ts` when the typed DTO arrives.

export interface CompanyRow {
  readonly id: string;
  readonly type: CompanyType;
  readonly name: string;
  readonly slug: string;
  /** Display region label, free string per artboard ("US-West", "EU"). */
  readonly region: string;
  readonly system_status: SystemStatus;
  /** Display date "YYYY-MM-DD"; also drives default most-recent sort. */
  readonly created_at: string;
  /** ISA: own count. MSA: aggregated across member ISAs (per artboard). */
  readonly properties_count: number;
  readonly users_count: number;
}

// ── Stub data hook ─────────────────────────────────────────────
// Returns `[]` today; a future BE ticket swaps this for a TanStack
// fetcher. No fabricated data in runtime.

function useCompanies(): readonly CompanyRow[] {
  return React.useMemo<readonly CompanyRow[]>(() => [], []);
}

// ── Compact-mode hook (matchMedia, mirrors DashboardPage) ──────

// ── Filter state + helpers ─────────────────────────────────────

type TypeFilter = 'all' | CompanyType;
type StatusFilter = 'all' | SystemStatus;

const TYPE_OPTIONS: ReadonlyArray<{ id: TypeFilter; label: string }> = [
  { id: 'all', label: 'All types' },
  { id: 'ISA', label: 'ISA' },
  { id: 'MSA', label: 'MSA' },
];

// SCRUM-311: filter options cover the full ISA lifecycle, in canonical
// order. The literal ids are checked against `StatusFilter`
// (= 'all' | SystemStatus), which is derived from the domain tuple — a
// removed/renamed status fails typecheck here.
const STATUS_OPTIONS: ReadonlyArray<{ id: StatusFilter; label: string }> = [
  { id: 'all', label: 'All statuses' },
  { id: 'onboarding', label: 'Onboarding' },
  { id: 'training', label: 'Training' },
  { id: 'testing', label: 'Testing' },
  { id: 'active', label: 'Active' },
  { id: 'closed', label: 'Closed' },
];

export function applyCompanyFilters(
  rows: readonly CompanyRow[],
  filters: { type: TypeFilter; status: StatusFilter; search: string },
): readonly CompanyRow[] {
  const q = filters.search.trim().toLowerCase();
  return rows.filter((row) => {
    if (filters.type !== 'all' && row.type !== filters.type) return false;
    if (filters.status !== 'all' && row.system_status !== filters.status)
      return false;
    if (q.length === 0) return true;
    return (
      row.name.toLowerCase().includes(q) ||
      row.slug.toLowerCase().includes(q) ||
      row.region.toLowerCase().includes(q)
    );
  });
}

export function sortCompaniesMostRecentFirst(
  rows: readonly CompanyRow[],
): readonly CompanyRow[] {
  // ISO/`YYYY-MM-DD` strings sort lexicographically == chronologically.
  return [...rows].sort((a, b) =>
    a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0,
  );
}

// ── Filter select (native, styled to the artboard FilterSelect) ─

function FilterSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  readonly label: string;
  readonly value: T;
  readonly options: ReadonlyArray<{ id: T; label: string }>;
  readonly onChange: (next: T) => void;
}): React.ReactElement {
  return (
    <div className="relative inline-flex">
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="cursor-pointer appearance-none rounded-full border border-solid border-[var(--border-default)] bg-[var(--bg-surface)] pl-3.5 pr-9 text-[13px] text-[var(--fg-primary)]"
        style={{ height: '38px' }}
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
      {/* Centered chevron overlay; pointer-events-none so the native
          select still opens on click. */}
      <Icon
        name="chevDown"
        size={12}
        aria-hidden
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--fg-tertiary)]"
      />
    </div>
  );
}

// ── Empty state ────────────────────────────────────────────────

function EmptyState({
  heading,
  action,
}: {
  readonly heading: string;
  readonly action: React.ReactNode;
}): React.ReactElement {
  return (
    <div
      className="mx-auto flex max-w-md flex-col items-center justify-center text-center"
      style={{ gap: '16px', padding: '48px 24px' }}
    >
      <p
        className="m-0 font-medium"
        style={{ fontSize: '15px', color: 'var(--fg-secondary)' }}
      >
        {heading}
      </p>
      {action}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────

export interface CompaniesListPageProps {
  /** matchMedia override for tests. */
  readonly compact?: boolean;
  /** Test/Storybook override for the stub `useCompanies` data. */
  readonly companiesOverride?: readonly CompanyRow[];
}

export function CompaniesListPage({
  compact: compactOverride,
  companiesOverride,
}: CompaniesListPageProps = {}): React.ReactElement {
  const compact = useCompactMode(compactOverride);
  const navigate = useNavigate();
  const hookData = useCompanies();
  const rawCompanies = companiesOverride ?? hookData;

  const [typeFilter, setTypeFilter] = React.useState<TypeFilter>('all');
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('all');
  const [searchInput, setSearchInput] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 200);
    return () => clearTimeout(t);
  }, [searchInput]);

  const sorted = React.useMemo(
    () => sortCompaniesMostRecentFirst(rawCompanies),
    [rawCompanies],
  );
  const filtered = React.useMemo(
    () =>
      applyCompanyFilters(sorted, {
        type: typeFilter,
        status: statusFilter,
        search: debouncedSearch,
      }),
    [sorted, typeFilter, statusFilter, debouncedSearch],
  );

  const totalCount = sorted.length;
  const isaCount = sorted.filter((c) => c.type === 'ISA').length;
  const msaCount = sorted.filter((c) => c.type === 'MSA').length;

  const clearFilters = (): void => {
    setTypeFilter('all');
    setStatusFilter('all');
    setSearchInput('');
  };

  return (
    <section
      aria-labelledby="companies-list-heading"
      className="mx-auto w-full"
      style={{ maxWidth: '1280px', padding: compact ? '24px 16px 80px' : '32px 48px 80px' }}
    >
      {/* Header */}
      <header
        className="flex flex-wrap items-end justify-between"
        style={{ gap: '16px', marginBottom: '24px' }}
      >
        <div>
          <p
            className="m-0 font-bold uppercase"
            style={{
              fontSize: '11px',
              letterSpacing: '1.2px',
              color: 'var(--fg-tertiary)',
              marginBottom: '6px',
            }}
          >
            Internal admin
          </p>
          <h1
            id="companies-list-heading"
            className="m-0 font-semibold"
            style={{
              fontSize: '28px',
              letterSpacing: '-0.4px',
              color: 'var(--fg-primary)',
            }}
          >
            Companies
          </h1>
          <p
            className="m-0"
            style={{
              fontSize: '14px',
              color: 'var(--fg-secondary)',
              marginTop: '6px',
            }}
          >
            {isaCount} ISAs · {msaCount} MSAs
          </p>
        </div>
        <div className="flex" style={{ gap: '8px' }}>
          {/* Export CSV — Phase-1 affordance; wires to the export
              endpoint when the BE ships. */}
          <Button variant="secondary">Export CSV</Button>
          <Button asChild variant="primary">
            <Link to="/admin/companies/new">+ New company</Link>
          </Button>
        </div>
      </header>

      <Card padding="none">
        {/* Filter bar */}
        <div
          className="flex flex-wrap items-center"
          style={{
            gap: '10px',
            padding: '14px 16px',
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          <div
            className="inline-flex items-center"
            style={{
              gap: '8px',
              height: '38px',
              padding: '0 14px',
              borderRadius: 'var(--radius-full)',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-default)',
              flex: '1 1 280px',
              maxWidth: '360px',
            }}
          >
            <Icon name="search" size={14} aria-hidden />
            {/* Inline borderless search pill. The kit TextField is a
                labeled field; its own doc sanctions composing a bare
                <input> for the hidden-label inline case. */}
            {/* eslint-disable-next-line @sparta/no-hand-rolled-input -- SCRUM-26: inline filter-bar search pill, not a labeled TextField (kit doc-sanctioned). */}
            <input
              type="search"
              aria-label="Search companies by name or slug"
              placeholder="Search name or slug"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="min-w-0 flex-1 border-none bg-transparent text-[13px] text-[var(--fg-primary)] outline-none"
            />
          </div>
          <FilterSelect
            label="Filter by type"
            value={typeFilter}
            options={TYPE_OPTIONS}
            onChange={setTypeFilter}
          />
          <FilterSelect
            label="Filter by status"
            value={statusFilter}
            options={STATUS_OPTIONS}
            onChange={setStatusFilter}
          />
          <span
            className="ml-auto"
            style={{ fontSize: '13px', color: 'var(--fg-tertiary)' }}
          >
            {filtered.length} of {totalCount}
          </span>
        </div>

        {/* List */}
        {totalCount === 0 ? (
          <EmptyState
            heading="No companies yet. Create your first."
            action={
              <Button asChild variant="primary">
                <Link to="/admin/companies/new">+ New company</Link>
              </Button>
            }
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            heading="No matches. Clear filters."
            action={
              <Button type="button" variant="secondary" onClick={clearFilters}>
                Clear filters
              </Button>
            }
          />
        ) : compact ? (
          <ul className="m-0 flex list-none flex-col p-0">
            {filtered.map((row, idx) => (
              <li key={row.id} className="m-0">
                <Link
                  to={`/admin/companies/${row.id}`}
                  aria-label={`Open ${row.name}`}
                  className="flex items-center no-underline"
                  style={{
                    padding: '14px 16px',
                    gap: '12px',
                    minHeight: '72px',
                    borderTop:
                      idx === 0 ? 'none' : '1px solid var(--border-subtle)',
                    color: 'var(--fg-primary)',
                  }}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center" style={{ gap: '8px', marginBottom: '4px' }}>
                      <TypeChip type={row.type} size="sm" />
                      <SystemStatusBadge
                        status={row.system_status}
                        size="sm"
                        showTooltip={false}
                      />
                    </div>
                    <p
                      className="m-0 truncate font-semibold"
                      style={{ fontSize: '14px', color: 'var(--fg-primary)' }}
                    >
                      {row.name}
                    </p>
                    <p
                      className="m-0 truncate"
                      style={{ fontSize: '12px', color: 'var(--fg-tertiary)', marginTop: '2px' }}
                    >
                      {row.region} · {row.properties_count} props · {row.users_count} users
                    </p>
                  </div>
                  <Icon name="chevRight" size={16} aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr
                className="text-left font-bold uppercase"
                style={{
                  color: 'var(--fg-tertiary)',
                  fontSize: '11px',
                  letterSpacing: '0.8px',
                  background: 'var(--bg-sunken)',
                }}
              >
                <th scope="col" style={{ padding: '12px 16px' }}>Company</th>
                <th scope="col" style={{ padding: '12px 16px' }}>Status</th>
                <th scope="col" style={{ padding: '12px 16px' }}>Region</th>
                <th scope="col" style={{ padding: '12px 16px' }}>Properties</th>
                <th scope="col" style={{ padding: '12px 16px' }}>Users</th>
                <th scope="col" style={{ padding: '12px 16px' }}>Created</th>
                <th scope="col" style={{ padding: '12px 16px', width: '40px' }}>
                  <span className="sr-only">Open</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const targetHref = `/admin/companies/${row.id}`;
                const handleRowClick = (
                  event: React.MouseEvent<HTMLTableRowElement>,
                ): void => {
                  const target = event.target as HTMLElement | null;
                  if (
                    target?.closest('a, button, [role="link"], [role="button"]') != null
                  ) {
                    return;
                  }
                  navigate(targetHref);
                };
                const handleRowKeyDown = (
                  event: React.KeyboardEvent<HTMLTableRowElement>,
                ): void => {
                  if (
                    (event.key === 'Enter' || event.key === ' ') &&
                    event.target === event.currentTarget
                  ) {
                    event.preventDefault();
                    navigate(targetHref);
                  }
                };
                return (
                  <tr
                    key={row.id}
                    onClick={handleRowClick}
                    onKeyDown={handleRowKeyDown}
                    className="cursor-pointer hover:bg-[var(--bg-sunken)]"
                    style={{ borderTop: '1px solid var(--border-subtle)' }}
                  >
                    <td style={{ padding: '14px 16px' }}>
                      <Link
                        to={targetHref}
                        aria-label={`Open ${row.name}`}
                        className="flex items-center no-underline"
                        style={{ gap: '10px', color: 'var(--fg-primary)' }}
                      >
                        <TypeChip type={row.type} size="sm" />
                        <span className="min-w-0">
                          <span
                            className="block truncate font-semibold"
                            style={{ fontSize: '14px', color: 'var(--fg-primary)' }}
                          >
                            {row.name}
                          </span>
                          <span
                            className="block truncate font-mono"
                            style={{ fontSize: '12px', color: 'var(--fg-tertiary)' }}
                          >
                            /{row.slug}
                          </span>
                        </span>
                      </Link>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <SystemStatusBadge
                        status={row.system_status}
                        size="sm"
                        showTooltip={false}
                      />
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--fg-secondary)' }}>
                      {row.region}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--fg-secondary)' }}>
                      {row.properties_count}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--fg-secondary)' }}>
                      {row.users_count}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--fg-tertiary)' }}>
                      {row.created_at}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <Icon name="chevRight" size={16} aria-hidden />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </section>
  );
}
